<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto h-[100px] w-auto" src="/img/spike-logo.svg" alt="Spike" />
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">I’ve forgotten my password</h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <div class="space-y-6 text-gray-700 text-sm">
          <p>What is the email address of your Spike account?</p>
          <input
            v-model="email"
            name="email"
            type="email"
            autocomplete="off"
            required
            :minlength="minLength"
            placeholder="example@email.com"
            @keyup="validateEmail()"
            class="appearance-none block w-full px-3 py-2 rounded-md border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary"
          />
          <p>Click the button below to reset your password. We will send you an email with a link to reset it.</p>

          <div class="flex justify-center items-center">
            <button
              type="submit"
              class="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-600 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
              :disabled="result === 'loading' || result === 'GenericResult.Success' || !valid"
              @click="forgotPassword()"
            >
              <div v-if="result === 'loading'">Sending ...</div>
              <div v-else-if="result === 'GenericResult.Success'">Sent</div>
              <div v-else>Send password reset email</div>
            </button>
          </div>

          <div class="flex w-full justify-center text-sm">
            <template v-if="result === 'loading'">
              <ScalingDots />
            </template>
            <template v-else-if="result === 'exception'">
              <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
              <span class="ml-2 text-red-600">failed to contact the server, please retry</span>
            </template>
            <template v-else-if="result === 'GenericResult.Fail'">
              <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
              <span class="ml-2 text-red-600">error</span>
            </template>
            <template v-else-if="result === 'GenericResult.Success'">
              <CheckCircleIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
              <span class="ml-2 text-green-600">success</span>
            </template>
            <template v-else>
              <!-- dummy - keep height -->
              <span>&nbsp;</span>
            </template>
          </div>

          <div><a :href="signInRoute" class="text-sm text-primary hover:text-primary-600 hover:underline">Back</a></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Url, ScreenRoute, GenericResult, Flow } from "@spike/app2-sitemap/src/auth.js";
import axios from "axios";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/solid";
import sleep from "@util/sleep";
import { onMounted, ref } from "vue";
import ScalingDots from "../../components/spinners/ScalingDots.vue";
import * as Nav from "../../navigation";
import { Screen } from "../../routes";
import * as Store from "../../store";

const signInRoute = ref("#" + ScreenRoute.SignIn);
const minLength = ref<number>(6);
const valid = ref<boolean>(false);
const email = ref<string | null>(null);
const result = ref<string | null>(null);

//#region ux

// https://codepen.io/CSWApps/pen/MmpBjV
const reg =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;

function validateEmail() {
  const e = email.value;
  valid.value = !e ? false : reg.test(e);
  result.value = null;
}

//#endregion

onMounted(async () => {
  // console.log("onMounted:", ScreenRoute.ForgotPasswordStart);
  Nav.startFlow(Flow.forgotPass);
});

//#region forgotPassword

async function forgotPassword() {
  try {
    const requestBody = {
      email: email.value,
    };
    result.value = "loading";
    const response = await axios.post(Url.server.shared.forgotPassword.sendReset, requestBody);
    // console.log("forgotPassword response:", response);
    const res = response.data;
    switch (res.result as GenericResult) {
      case GenericResult.Success:
        forgotPasswordSuccess();
        break;
      case GenericResult.Fail:
        forgotPasswordFail();
        break;
      default:
        forgotPasswordException();
        break;
    }
  } catch (e) {
    // console.error("forgotPassword failed.sendReset:", e);
    forgotPasswordException();
  }
}

async function forgotPasswordTest() {
  result.value = "loading";
  await sleep(500);

  // testing
  // forgotPasswordSuccess();
  // forgotPasswordFail();
  forgotPasswordException();
}

async function forgotPasswordSuccess() {
  result.value = "GenericResult.Success";
  await sleep(1000);
  Store.setForgotEmail(email.value as string);
  Nav.complete(Screen.ForgotPasswordStart);
  Nav.setActive(Screen.ForgotPasswordVerify, true);
}

async function forgotPasswordFail() {
  result.value = "GenericResult.Fail";
}

async function forgotPasswordException() {
  result.value = "exception";
}

//#endregion
</script>
